import type { ISerialize } from '@/objects/ISerialize';
import { formatDateIso } from '@/utilities/dateutils';

export class Transaction implements ISerialize {
    public ID: number;

    public Bib: number | null;

    public Content: string | null;

    public Created: Date;

    public DeviceID: string | null;

    public Messages: string | null;

    public TransactionTime: Date;

    public Type: string | null;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Bib?: number | null;
        Content?: string | null;
        Created?: Date;
        DeviceID?: string | null;
        Messages?: string | null;
        TransactionTime?: Date;
        Type?: string | null;
    }) {
        this.ID = values.ID ?? 0;

        this.Bib = values.Bib ?? null;

        this.Content = values.Content ?? null;

        this.Created = values.Created ?? new Date('1970-01-01T00:00:00.000');

        this.DeviceID = values.DeviceID ?? '';

        this.Messages = values.Messages ?? null;

        this.TransactionTime = values.TransactionTime ?? new Date('1970-01-01T00:00:00.000');

        this.Type = values.Type ?? null;
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Bib": ${JSON.stringify(this.Bib)}` +
            ',' +
            `"Content": ${JSON.stringify(this.Content)}` +
            ',' +
            `"Created": ${'"' + formatDateIso(this.Created) + '"'}` +
            ',' +
            `"DeviceID": ${JSON.stringify(this.DeviceID)}` +
            ',' +
            `"Messages": ${JSON.stringify(this.Messages)}` +
            ',' +
            `"TransactionTime": ${'"' + formatDateIso(this.TransactionTime) + '"'}` +
            ',' +
            `"Type": ${JSON.stringify(this.Type)}` +
            '}'
        );
    }

    /**
     * Creates an instance of Transaction from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Bib?: number | null;
        Content?: string | null;
        Created?: Date;
        DeviceID?: string | null;
        Messages?: string | null;
        TransactionTime?: Date;
        Type?: string | null;
    }): Transaction {
        return new Transaction(values);
    }

    public static _FromServerJson(json: any): Transaction {
        return new Transaction({
            ID: json.ID,
            Bib: json.Bib ?? null,
            Content: json.Content ?? '',
            Created: json.Created instanceof Date ? json.Created : new Date(json.Created),
            DeviceID: json.DeviceID ?? '',
            Messages: json.Messages ?? '',
            TransactionTime: json.TransactionTime instanceof Date ? json.TransactionTime : new Date(json.TransactionTime),
            Type: json.Type ?? '',
        });
    }

    /**
     * Creates an instance of Transaction from another instance of Transaction.
     * @param other The Transaction you want to clone
     * @returns A cloned Transaction
     */
    public static FromOther(other: Transaction): Transaction {
        return Transaction._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return Transaction.FromOther(this);
    }

    static NAME_OF_CLASS = 'Transaction';
}
